import type { IClient } from '../type';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { API_URL, ROLE, message } from 'constant';
import { ClientsCredentialsState, loginState } from 'global-stores';
import { useTrackEvents } from 'helpers';
import { useNetwork, useNotification, useTimezoneDate } from 'hooks';
import {
	Blank,
	CopyIcon,
	CopyRow,
	Field,
	Kebab,
	Modal,
	PageHead,
	Pager,
	SearchBox,
} from 'views/design-system';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';

import { ago, byNewest, search, summarise } from './derive';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './developer-keys-deck.scss';

const PAGE_SIZE = 15;
const NAME_MAX = 32;
const NAME_MIN = 3;

interface IDeveloperKeysDeck {
	loading?: boolean;
}

/**
 * Developer Keys on the v2 design system.
 *
 * The screen it replaces (`views/credential/credential.tsx`) is untouched and
 * still exported — nothing was deleted to make room. Same endpoints, same
 * recoil atom, same permission gate; what changes is that the three row icons
 * become one kebab, the secret gets a readable modal instead of a cramped one,
 * and the page reports the state of the key set above the table.
 */
export const DeveloperKeysDeck: FC<IDeveloperKeysDeck> = ({ loading = false }) => {
	const { successNotification, errorNotification } = useNotification();
	const { formatDate } = useTimezoneDate();
	const { trackEvents } = useTrackEvents();
	const { checkUserWritePermission } = useUserRoles();

	const [keys, setKeys] = useRecoilState(ClientsCredentialsState);
	const { role } = useRecoilValue(loginState);

	const { post, loading: creating } = useNetwork();
	const { patch, loading: saving } = useNetwork();
	const { remove, loading: deleting } = useNetwork();

	const [typed, setTyped] = useState('');
	const [page, setPage] = useState(1);

	/** The row a modal is about. Held as the key itself so a list refresh
	 *  cannot leave the modal pointing at a different row by index. */
	const [viewing, setViewing] = useState<IClient | null>(null);
	const [renaming, setRenaming] = useState<IClient | null>(null);
	const [deletingKey, setDeletingKey] = useState<IClient | null>(null);
	const [creatingOpen, setCreatingOpen] = useState(false);
	const [draftName, setDraftName] = useState('');

	const {
		CREDS_SUCCESS,
		DELETE_MESSAGE,
		UPDATE_SUCCESS_MESSAGE,
		COPY_TOASTER_MESSAGE,
		VALID_NAME_MESSAGE,
		DUPLICATE_NAME_MESSAGE,
	} = useMemo(() => message, []);

	const canWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.DevelopersKeys),
		[checkUserWritePermission]
	);
	/** A MANAGER can see the page but may not act on it — the old page's rule. */
	const isManager = role === ROLE.MANAGER;

	const rows = useMemo(() => byNewest(search(keys as IClient[], typed)), [keys, typed]);
	const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
	const shown = useMemo(
		() => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
		[rows, page]
	);
	const stats = useMemo(() => summarise(keys as IClient[]), [keys]);

	const copy = useCallback(
		(value: string) => {
			navigator.clipboard.writeText(value);
			successNotification(COPY_TOASTER_MESSAGE);
		},
		[COPY_TOASTER_MESSAGE, successNotification]
	);

	/* ------------------------------------------------------------- create */

	const createKey = useCallback(async () => {
		trackEvents('create-new-credential', {});
		const resp = await post(`${API_URL.CREDENTIAL}`, {});
		setCreatingOpen(false);
		/* The API answers a refusal with a 200 carrying `message` rather than an
		 * error status, so a successful-looking response still has to be read. */
		if (!resp || resp.message) {
			errorNotification(resp?.message ?? message.SomethingWentWrongMessage);
			return;
		}
		setKeys(prev => [...prev, resp]);
		successNotification(CREDS_SUCCESS);
	}, [
		CREDS_SUCCESS,
		errorNotification,
		post,
		setKeys,
		successNotification,
		trackEvents,
	]);

	/* ------------------------------------------------------------- rename */

	const duplicate = useMemo(() => {
		if (!renaming) return false;
		const wanted = draftName.trim().toLowerCase();
		return (keys as IClient[]).some(
			k => k._id !== renaming._id && (k.name ?? '').toLowerCase() === wanted
		);
	}, [draftName, keys, renaming]);

	const nameError = useMemo(() => {
		if (draftName.trim().length < NAME_MIN) return VALID_NAME_MESSAGE;
		if (duplicate) return DUPLICATE_NAME_MESSAGE;
		return '';
	}, [DUPLICATE_NAME_MESSAGE, VALID_NAME_MESSAGE, draftName, duplicate]);

	const saveName = useCallback(async () => {
		if (!renaming || nameError) return;
		const resp = await patch(`${API_URL.CREDENTIAL}/${renaming._id}`, {
			name: draftName.trim(),
		});
		if (!resp) return;
		setKeys(prev =>
			prev.map(k => ((k as IClient)._id === renaming._id ? { ...k, ...resp } : k))
		);
		successNotification(UPDATE_SUCCESS_MESSAGE);
		setRenaming(null);
	}, [
		UPDATE_SUCCESS_MESSAGE,
		draftName,
		nameError,
		patch,
		renaming,
		setKeys,
		successNotification,
	]);

	/* ------------------------------------------------------------- delete */

	const confirmDelete = useCallback(async () => {
		if (!deletingKey) return;
		const resp = await remove(`${API_URL.CREDENTIAL}/${deletingKey._id}`);
		if (!resp) return;
		setKeys(prev => prev.filter(k => (k as IClient)._id !== deletingKey._id));
		successNotification(DELETE_MESSAGE);
		setDeletingKey(null);
	}, [DELETE_MESSAGE, deletingKey, remove, setKeys, successNotification]);

	/** A page that has scrolled past the end of a shrinking list shows nothing. */
	useEffect(() => {
		if (page > pages) setPage(pages);
	}, [page, pages]);

	const menuFor = useCallback(
		(key: IClient) => {
			const items = [
				{ label: 'View credentials', onClick: () => setViewing(key) },
			];
			if (canWrite && !isManager) {
				items.push({
					label: 'Rename',
					onClick: () => {
						setDraftName(key.name ?? '');
						setRenaming(key);
					},
				});
				/* The last key is not deletable: an account with no credential
				 * cannot call the API at all. The old page disabled the icon; the
				 * menu drops the entry, which cannot be mistaken for "broken". */
				if (keys.length > 1) {
					items.push({ label: 'Delete', onClick: () => setDeletingKey(key) });
				}
			}
			return items;
		},
		[canWrite, isManager, keys.length]
	);

	const busy = loading || creating;
	const hasKeys = keys.length > 0;

	return (
		<div className="sv2-scope sv2 keys-deck">
			<PageHead
				title="Developer Keys"
				sub={
					<>
						{keys.length} {keys.length === 1 ? 'key' : 'keys'} &middot; credentials
						for the Simplici REST API &middot; a secret is readable any time from
						its own row
					</>
				}
				action={
					canWrite && !isManager ? (
						<button
							className="cta"
							type="button"
							disabled={busy}
							onClick={() => setCreatingOpen(true)}
						>
							{creating ? 'Creating…' : 'Create new key'}
						</button>
					) : undefined
				}
			/>

			{hasKeys && (
				<div className="strip estrip">
					<div className="tile">
						<div className="l">Keys</div>
						<div className="n">{stats.count}</div>
						<div className="d">usable against the API right now</div>
					</div>
					<div className="tile">
						<div className="l">Newest</div>
						<div className="n bl">{ago(stats.newest?.createdAt)}</div>
						<div className="d">
							{stats.newest?.name ?? '--'}
							{stats.newest ? ` · ${formatDate(stats.newest.createdAt, 'MMM dd, yyyy')}` : ''}
						</div>
					</div>
					<div className="tile">
						<div className="l">Oldest</div>
						<div className="n gy">{ago(stats.oldest?.createdAt)}</div>
						<div className="d">
							{stats.oldest?.name ?? '--'}
							{stats.oldest ? ` · ${formatDate(stats.oldest.createdAt, 'MMM dd, yyyy')}` : ''}
						</div>
					</div>
					<div className="tile">
						<div className="l">Last renamed</div>
						<div className="n gy">
							{stats.renamed ? ago(stats.renamed.updatedAt) : '--'}
						</div>
						<div className="d">
							{stats.renamed
								? `${stats.renamed.name} · ${formatDate(stats.renamed.updatedAt, 'MMM dd, yyyy')}`
								: 'no key has been renamed'}
						</div>
					</div>
				</div>
			)}

			{hasKeys && (
				<div className="ctl">
					<SearchBox
						value={typed}
						onChange={v => {
							setTyped(v);
							setPage(1);
						}}
						placeholder="Search name or client ID"
						title="Searches the key name and its client ID"
					/>
					<Pager page={page} pages={pages} onChange={setPage} />
				</div>
			)}

			<div className="panel">
				<div className="ph">
					API credentials
					<span className="r">
						{rows.length} {rows.length === 1 ? 'key' : 'keys'}
						{typed ? ` of ${keys.length}` : ''}
					</span>
				</div>

				{busy && !hasKeys ? (
					<div className="sv2-loading">Loading developer keys…</div>
				) : !hasKeys ? (
					<Blank
						title="No developer keys yet"
						description={
							<>
								A developer key is the client ID and secret your integration
								uses to authenticate against the Simplici REST API. Create one
								to start calling it.
							</>
						}
						action={
							canWrite && !isManager ? (
								<button
									className="btn pri"
									type="button"
									disabled={creating}
									onClick={() => setCreatingOpen(true)}
								>
									{creating ? 'Creating…' : 'Create your first key'}
								</button>
							) : undefined
						}
					/>
				) : !rows.length ? (
					<div className="sv2-empty">
						No key matches &ldquo;{typed}&rdquo;.
					</div>
				) : (
					<div className="tscroll">
						<table className="stbl ktbl">
							<thead>
								<tr>
									<th>Name</th>
									<th>Client ID</th>
									<th>Created</th>
									<th>Last updated</th>
									<th className="accol">Actions</th>
								</tr>
							</thead>
							<tbody>
								{shown.map(key => (
									<tr key={key._id}>
										<td>
											<span className="nm">{key.name || '--'}</span>
										</td>
										<td>
											<span className="idcell">
												{key.clientId || '--'}
												{!!key.clientId && (
													<button
														className="ccopy"
														type="button"
														title="Copy client ID"
														onClick={() => copy(key.clientId)}
													>
														<CopyIcon />
													</button>
												)}
											</span>
										</td>
										<td>{formatDate(key.createdAt, 'MMM dd, yyyy')}</td>
										<td>
											{key.updatedAt
												? formatDate(key.updatedAt, 'MMM dd, yyyy')
												: '--'}
										</td>
										<td className="accol">
											<Kebab items={menuFor(key)} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{hasKeys && (
				<div className="foot">
					The last remaining key cannot be deleted &mdash; an account with no
					credential cannot call the API at all. <code>POST /credentials</code>{' '}
					mints a key; its secret is returned with it and stays readable from its
					own row afterwards.
				</div>
			)}

			{/* ---------------------------------------------- view credentials */}
			<Modal
				open={!!viewing}
				title={viewing?.name || 'Credentials'}
				onClose={() => setViewing(null)}
				sub={
					viewing
						? `Created ${formatDate(viewing.createdAt, 'MMM dd, yyyy, h:mm a')} · use this pair to authenticate against the REST API`
						: undefined
				}
				footer={
					<button className="btn" type="button" onClick={() => setViewing(null)}>
						Close
					</button>
				}
			>
				{!!viewing?.clientId && (
					<CopyRow label="Client ID" value={viewing.clientId} onCopy={copy} />
				)}
				{!!viewing?.clientSecret && (
					<CopyRow
						label="Client secret"
						value={viewing.clientSecret}
						onCopy={copy}
						note="Treat this like a password — anyone holding it can act as your account."
					/>
				)}
			</Modal>

			{/* --------------------------------------------------------- rename */}
			<Modal
				open={!!renaming}
				title="Rename key"
				onClose={() => setRenaming(null)}
				sub="The name is a label for your team — the client ID and secret do not change."
				footer={
					<>
						<button className="btn" type="button" onClick={() => setRenaming(null)}>
							Cancel
						</button>
						<button
							className="btn pri"
							type="button"
							disabled={!!nameError || saving}
							onClick={saveName}
						>
							{saving ? 'Saving…' : 'Save'}
						</button>
					</>
				}
			>
				<Field
					label="Name"
					value={draftName}
					onChange={setDraftName}
					max={NAME_MAX}
					autoFocus
					error={draftName ? nameError : ''}
					hint="At least 3 characters, and not a name another key already uses."
				/>
				{!!renaming?.clientId && (
					<CopyRow label="Client ID" value={renaming.clientId} onCopy={copy} />
				)}
			</Modal>

			{/* --------------------------------------------------------- delete */}
			<Modal
				open={!!deletingKey}
				title="Delete this key?"
				onClose={() => setDeletingKey(null)}
				size="narrow"
				sub={
					<>
						Any integration still sending <b>{deletingKey?.name}</b> starts
						failing immediately. This cannot be undone.
					</>
				}
				footer={
					<>
						<button
							className="btn"
							type="button"
							onClick={() => setDeletingKey(null)}
						>
							Cancel
						</button>
						<button
							className="btn danger"
							type="button"
							disabled={deleting}
							onClick={confirmDelete}
						>
							{deleting ? 'Deleting…' : 'Delete key'}
						</button>
					</>
				}
			/>

			{/* --------------------------------------------------------- create */}
			<Modal
				open={creatingOpen}
				title="Create a new key"
				onClose={() => setCreatingOpen(false)}
				size="narrow"
				sub="A key is minted with a generated name you can rename afterwards."
				footer={
					<>
						<button
							className="btn"
							type="button"
							onClick={() => setCreatingOpen(false)}
						>
							Cancel
						</button>
						<button
							className="btn pri"
							type="button"
							disabled={creating}
							onClick={createKey}
						>
							{creating ? 'Creating…' : 'Create key'}
						</button>
					</>
				}
			/>
		</div>
	);
};

export default DeveloperKeysDeck;
